/**
 * Normalizes and returns result page data for both Result and PreviousResult.
 * When isPreviousResult is true, flattens result.results onto result so the
 * rest of the UI can use the same paths (resultData.countryCode, etc.).
 */
import { useMemo } from "react";
import { useSelector } from "react-redux";

export function useResultPageData(isPreviousResult) {
  const resultFromStore = useSelector((state) =>
    isPreviousResult
      ? state?.ResultReducer?.previousResult ?? []
      : state?.ResultReducer?.result ?? []
  );

  const oldFormReducer = useSelector((state) => state?.OldFormReducer ?? {});
  const skillRelevance = useSelector((state) => state?.ResultReducer?.skill_relevance ?? "");
  const oldFormSubSkill = useSelector((state) => state?.OldFormReducer?.subSkill ?? []);
  const globalResultPageSections = useSelector((state) => state?.ResultReducer?.resultPageSections ?? []);

  // Memoize resultData so it maintains referential equality when the underlying
  // Redux data hasn't changed. Without this, the spread creates a new object
  // every render, which causes useEffect loops in consumers.
  const resultData = useMemo(() => {
    if (!resultFromStore || (Array.isArray(resultFromStore) && resultFromStore.length === 0)) {
      return resultFromStore;
    }
    if (isPreviousResult && resultFromStore?.results) {
      const { results, ...rest } = resultFromStore;
      return {
        ...rest,
        ...results,
        userData: resultFromStore.userData,
        results: results,
      };
    }
    return resultFromStore;
  }, [resultFromStore, isPreviousResult]);

  const userForm = isPreviousResult ? resultData?.userData : oldFormReducer;
  const userData = isPreviousResult ? resultData?.userData : resultData?.userFormData;
  const relevanceResult = isPreviousResult ? (resultData?.userData?.relevanceResult ?? "") : skillRelevance;
  const subSkillsData = isPreviousResult ? (resultData?.userData?.subSkill ?? []) : oldFormSubSkill;
  // For previous result, use sections stored with that result if any; otherwise fall back to global
  const resultPageSections = isPreviousResult
    ? (resultData?.resultPageSections ?? globalResultPageSections)
    : globalResultPageSections;

  return {
    resultData,
    userForm,
    userData,
    relevanceResult,
    subSkillsData,
    resultPageSections,
  };
}
